import React from 'react';
import { NextPage } from 'next';
import { PageHeadProps } from '../client/components/page-head/PageHead';
import { RelatedArticleItem } from '../client/components/related-articles/RelatedArticles';
import { PAGINATED_GUIDES_QUERY, PAGINATED_GUIDES_QUERY_BY_CATEGORY } from '../client/graphql/queries/guide';
import GuidesPage, { GuidesPageProps, PAGE_SIZE } from '../client/templates/common/guides-page/GuidesPage';
import { getGraphCmsEnumValues, GraphEnum } from '../client/utils/graphcms/categoriesUtils';
import {
  QueryObject, slugIt,
} from '../client/utils/links';
import { buildArticlesURLObject } from '../client/utils/links/articles';
import { buildPageableSEO } from '../client/utils/seo/seo';
import { Adv } from '../domain/adv';
import { ArticleType } from '../domain/articleType';
import { Target, Category, AdvFluidType } from '../domain/graphcms';
import { getAdvByType } from '../services/advs/advs';
import { toArticleCategoryItems } from '../client/utils/categories/toArticleCategoryItems';
import { CategoryItem } from '../client/types';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { withGetServerSideError, withError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';
import httpErrors from '../client/utils/error/http/httpErrors';
import { ASSET_BY_IDS } from '../client/graphql/queries/asset';

export interface GuidesSerpProps {
  query: QueryObject,
  title: string,
  category?: string,
  categories: CategoryItem[],
  SEO: PageHeadProps['SEO'],
  advSerp: Adv,
  advSide: Adv,
  page: number,
  totalPages: number,
  totalItems: number,
  guides: any[],
  bottomBar: GuidesPageProps['bottomBar'],
  promotionalLinks: Omit<RelatedArticleItem, 'onClick'>[],
}

export const GuidesSerp: NextPage<GuidesSerpProps> = ({
  query,
  title,
  category,
  SEO,
  advSerp,
  advSide,
  page,
  totalPages,
  totalItems,
  guides,
  categories,
  bottomBar,
  promotionalLinks,
}) => (
  <GuidesPage
    query={query}
    title={title}
    category={category}
    categories={categories}
    advSerp={advSerp}
    advSide={advSide}
    articleType={ArticleType.GUIDE}
    SEO={SEO}
    guides={guides}
    page={page}
    totalPages={totalPages}
    totalItems={totalItems}
    promotionalLinks={promotionalLinks}
    bottomBar={bottomBar}
  />
);

export const getServerSideProps = withGetServerSideError<GuidesSerpProps>(async ({ query }) => {
  const graphQLClient = initGraphQLClient();
  const page = query.p ? Number.parseInt(query.p as string, 10) : 1;
  const articlesURLObject = buildArticlesURLObject({
    category: query.category,
  }, ArticleType.GUIDE);

  if (Number.isNaN(page) || page < 1) {
    throw new httpErrors.BadRequest(`Invalid page parameter ${page}`);
  }

  const categories = (await getGraphCmsEnumValues(graphQLClient, GraphEnum.GUIDE_CATEGORY))
    .filter((c) => c !== Category.AGENTI);

  let graphqlQuery: any = {
    query: PAGINATED_GUIDES_QUERY,
    variables: {
      skip: PAGE_SIZE * (page - 1),
      first: PAGE_SIZE,
      target: [Target.COMPRATORE, Target.VENDITORE],
    },
  };

  let category;
  if (query.category) {
    category = categories.find((value) => slugIt(value) === query.category);

    if (!category) {
      return {
        redirect: {
          destination: buildArticlesURLObject({}, ArticleType.GUIDE).as,
          permanent: false,
        },
      };
    }

    graphqlQuery = {
      query: PAGINATED_GUIDES_QUERY_BY_CATEGORY,
      variables: {
        ...graphqlQuery.variables,
        category,
      },
    };
  }

  const [
    { SEO: seoTemplate, bottomBar, promotionalLinks },
    adv,
    { data },
  ] = await Promise.all([
    importDynamicData('pages/guides-serp.yml'),
    getAdvByType(AdvFluidType.SELL_PROPERTY),
    graphQLClient.query(graphqlQuery),
  ]);

  const enrichedPromotionalLinks = await graphQLClient.query({
    query: ASSET_BY_IDS,
    variables: {
      ids: promotionalLinks.map((link) => link.thumbnail.handle),
    },
  });

  const promotionalLinksAssetUrls = enrichedPromotionalLinks.data.assets.reduce((acc, asset) => {
    acc[asset.handle] = asset.url;
    return acc;
  }, {});

  const totalItems = data.total.aggregate.count;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  if (page > totalPages && totalPages > 0) {
    throw new httpErrors.NotFound(`Page parameter ${page} greater than total pages ${totalPages}`);
  }

  const pageable = {
    page,
    first: page === 1,
    last: page === totalPages,
    hasResults: data.guides.length > 0,
  };

  return {
    props: {
      query: {
        ...query,
        category: query.category ?? null,
      },
      title: data.guideSerp?.title || 'Guide e consigli per compravendite immobiliari di successo',
      category: category ?? null,
      categories: [
        {
          id: 'tutte-le-guide',
          label: 'Tutte le guide',
          urlObject: buildArticlesURLObject({}, ArticleType.GUIDE),
        },
        ...toArticleCategoryItems(categories),
      ],
      advSerp: adv,
      advSide: adv,
      SEO: buildPageableSEO({
        ...(data.guideSerp?.seo || seoTemplate),
        urlObject: articlesURLObject,
      }, pageable),
      guides: data.guides,
      page,
      totalPages,
      totalItems,
      bottomBar,
      promotionalLinks: promotionalLinks?.map((link) => ({
        ...link,
        thumbnail: {
          ...link.thumbnail,
          url: promotionalLinksAssetUrls[link.thumbnail.handle],
        },
      })) ?? [],
    },
  };
});

export default withError(GuidesSerp);
